import {
    Button,
    DropDown,
    Form,
    Modal,
    Pending,
    useErrorState,
    ValidatedDropDownList,
    ValidatedDynamicList,
    ValidatedInput
} from '@finbeat/react-toolkit';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import { OrderStatus, OrderStatusTimeoutUpdate } from 'models/api/orderStatus';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useOrderStatusWorkplaceState } from './OrderStatus';
import { RoleSelectForm, RoleSelectFormWrapper } from './OrderStatusRoleForm';
import { TimeoutForm, TimeoutFormWrapper } from './TimeoutForm';

type OrderStatusChangeModalProps = {
    isOpen: boolean;
    toggle: () => void;
};

const OrderStatusChangeModal = ({ isOpen = false, toggle }: OrderStatusChangeModalProps) => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const { handleRequestError } = useErrorState();
    const { currentItem, changeItemMutation } = useOrderStatusWorkplaceState();

    const [clientStatusList, setClientStatusList] = useState<DropDown[]>([]);
    const [toStatusList, setToStatusList] = useState<DropDown[]>([]);
    const [iconsList, setIconsList] = useState<DropDown[]>([]);
    const [menuSectionList, setMenuSectionList] = useState<DropDown[]>([]);

    const { isFetching } = useQuery(
        'orderStatusSelectLists',
        () =>
            getSelectList(
                api,
                ['ClientOrderStatus', 'ToOrderStatus', 'Icons', 'MenuSection'],
                i18n.language
            ),
        {
            onSuccess: (data) => {
                if (data) {
                    setClientStatusList(data['ClientOrderStatus'] ?? []);
                    setToStatusList(data['ToOrderStatus'] ?? []);
                    setIconsList(data['Icons'] ?? []);
                    setMenuSectionList(data['MenuSection'] ?? []);
                }
            },
            onError: handleRequestError,
        }
    );

    return (
        <Modal size="lg" isOpen={isOpen} toggle={toggle} scrollable>
            {currentItem ? (
                <Form<OrderStatus>
                    onSubmit={(data) => {
                        changeItemMutation.mutate(data);
                        toggle();
                    }}
                    className="d-contents"
                    onInvalid={(e) => console.log('onInvalid', e)}
                    defaultValues={currentItem}
                    clearAfterSubmit={false}
                >
                    <ModalHeader toggle={toggle}>
                        {currentItem?.clientStatusCode} - {currentItem?.name}
                    </ModalHeader>

                    <ModalBody className="custom-scrollbar">
                        <Row>
                            <Col>
                                <ValidatedDropDownList<OrderStatus>
                                    items={clientStatusList}
                                    name="clientStatusCode"
                                    label={t('BackOfficeClientStatus')}
                                    isLoading={isFetching}
                                />
                            </Col>
                            <Col>
                                <ValidatedDropDownList<OrderStatus>
                                    items={toStatusList}
                                    name="toStatusCode"
                                    label={t('BackOfficeToStatus')}
                                    isLoading={isFetching}
                                />
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col>
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="isFinal"
                                    label={t('BackOfficeFinalStatus')}
                                />
                            </Col>
                            <Col>
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="isClientFinalStatus"
                                    label={t('BackOfficeClientFinalStatus')}
                                />
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col>
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="canEditOrder"
                                    label={t('BackOfficeEditingOrder')}
                                />
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="canPassToScoring"
                                    label={t('BackOfficePassToScoring')}
                                />
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="canTransfToOper"
                                    label={t('BackOfficeTransfToOper')}
                                />
                            </Col>
                            <Col>
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="canUpdDocs"
                                    label={t('BackOfficeUpdDocs')}
                                />
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="canEditServices"
                                    label={t('BackOfficeEditingAdditionalServices')}
                                />
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col>
                                <ValidatedDropDownList<OrderStatus>
                                    name="icon"
                                    label={t('BackOfficeOrderStatusIcon')}
                                    items={iconsList.map<DropDown>((x) => ({
                                        id: x.id,
                                        name: x.name,
                                        label: (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={x.id?.toString().split('_') as IconProp}
                                                />{' '}
                                                {x.id}
                                            </>
                                        ),
                                    }))}
                                    isLoading={isFetching}
                                />
                            </Col>
                            <Col>
                                <ValidatedDropDownList<OrderStatus>
                                    name="menuSection"
                                    label={t('BackOfficeOrderStatusMenuSection')}
                                    items={menuSectionList}
                                    isLoading={isFetching}
                                />
                            </Col>
                            <Col>
                                <ValidatedInput<OrderStatus>
                                    name="menuSorting"
                                    label={t('BackOfficeOrderStatusMenuSorting')}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="isImportant"
                                    label={t('BackOfficeIsImportant')}
                                />
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="isAssign"
                                    label={t('BackOfficeIsAssign')}
                                />
                            </Col>
                        </Row>
                        <hr />
                        <ValidatedDynamicList
                            formComponent={(params) => <RoleSelectForm {...params} />}
                            wrapper={RoleSelectFormWrapper}
                            emptyRecord=""
                            name="roles"
                            compareFunc={(a, b) => a === b}
                        />
                        <hr />
                        <div className="row">
                            <div className="col">
                                <ValidatedInput<OrderStatus>
                                    type="checkbox"
                                    name="isHaveTimeouts"
                                    label={t('BackOfficeTimeouts')}
                                />
                            </div>
                        </div>
                        <ValidatedDynamicList<OrderStatus, OrderStatusTimeoutUpdate>
                            formComponent={TimeoutForm}
                            wrapper={TimeoutFormWrapper}
                            emptyRecord={{
                                timeout: '',
                                action: '',
                                actionAdditionalInfo: '',
                                actionAdditionalData: '',
                            }}
                            name="timeouts"
                            compareFunc={(a, b) => a?.action === b?.action}
                        />
                    </ModalBody>

                    <ModalFooter>
                        <Button color="secondary" onClick={toggle}>
                            <Trans i18nKey="BackOfficeCancel" />
                        </Button>
                        <Button color="success" type={'submit'}>
                            <Trans i18nKey="BackOfficeSave" />
                        </Button>
                    </ModalFooter>
                </Form>
            ) : (
                <Pending />
            )}
        </Modal>
    );
};

export default OrderStatusChangeModal;
